/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { uploadedFileSchema } from '../uploadedFile.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';
import { ProductStatus } from './product.js';

const productStatusValues = Object.values(ProductStatus);

export const reorderPolicySchema = Yup.object().shape({
  enabled: Yup.boolean().default(false).required('reorderPolicy.enabled is required'),
  triggerQuantity: Yup.number().integer().min(0).nullable()
    .default(null),
  targetLevel: Yup.number().integer().min(0).nullable()
    .default(null),
  updatedAt: YupFirestoreTimestamp.nullable(),
  updatedBy: Yup.object().shape({
    uid: Yup.string().required('reorderPolicy.updatedBy.uid is required'),
    email: Yup.string().email().required('reorderPolicy.updatedBy.email is required'),
  }).nullable().default(null),
}).nullable().default(null)
  .test(
    'enabled-policy-has-quantities',
    'Enabled reorder policies require target level greater than trigger quantity',
    (policy) => {
      if (!policy?.enabled) return true;
      return Number.isInteger(policy.triggerQuantity)
        && Number.isInteger(policy.targetLevel)
        && policy.targetLevel > policy.triggerQuantity;
    },
  );

export const productVersionSchema = Yup.object().shape({
  status: Yup.string()
    .oneOf(productStatusValues)
    .default(ProductStatus.DRAFT)
    .required('Product version status is required'),
  projectId: Yup.string()
    .matches(/^P\d{3}$/, 'Internal project ID must start with "P" and be followed by 3 digits')
    .required('Internal project ID is required'),
  projectDocId: Yup.string().nullable(),
  pPartId: Yup.string().required('pPart ID is required'),
  pPartDocId: Yup.string().nullable(),
  unitPrice: Yup.number().min(0).required('Unit price is required'),
  leadtimeDays: Yup.number().integer().min(0).nullable(),
  /** 3D preview GLB uploaded via the admin FileUploader (single file). */
  previewGlb: Yup.array().of(uploadedFileSchema).nullable().default(null),
  reorderPolicy: reorderPolicySchema,
  effectiveAt: YupFirestoreTimestamp.nullable(),
  retiredAt: YupFirestoreTimestamp.nullable(),
  createdAt: YupFirestoreTimestamp.nullable(),
  createdBy: Yup.object().shape({
    uid: Yup.string().required('createdBy.uid is required'),
    email: Yup.string().email().required('createdBy.email is required'),
  }).required('createdBy is required'),
});

/**
 * @typedef {Yup.InferType<typeof productVersionSchema>} ProductVersionSchema
 * @typedef {BaseDocData & ProductVersionSchema} ProductVersionDocData
 */

export default class ProductVersion {
  /** @type {ProductVersionDocData['validation']} */ validation = 'passed';
  /** @type {ProductVersionDocData['id']} */ id;

  /**
   * @param {ProductVersionDocData} param0 Raw Firestore product version document.
   */
  constructor({
    id,
    status = ProductStatus.DRAFT,
    projectId,
    projectDocId = null,
    pPartId,
    pPartDocId = null,
    unitPrice,
    leadtimeDays = null,
    previewGlb = null,
    reorderPolicy = null,
    effectiveAt = null,
    retiredAt = null,
    createdAt,
    createdBy,
  }) {
    const validatedData = productVersionSchema.validateSync({
      status,
      projectId,
      projectDocId,
      pPartId,
      pPartDocId,
      unitPrice,
      leadtimeDays,
      previewGlb,
      reorderPolicy,
      effectiveAt,
      retiredAt,
      createdAt,
      createdBy,
    });

    this.id = id || null;
    Object.assign(this, validatedData);
  }

  /**
   * @returns {Omit<ProductVersionDocData, 'id'>}
   */
  toFirestore({ serverTimestamp }) {
    return {
      status: this.status,
      projectId: this.projectId,
      projectDocId: this.projectDocId || null,
      pPartId: this.pPartId,
      pPartDocId: this.pPartDocId || null,
      unitPrice: this.unitPrice,
      leadtimeDays: this.leadtimeDays ?? null,
      previewGlb: this.previewGlb?.length ? this.previewGlb : null,
      reorderPolicy: this.reorderPolicy || null,
      effectiveAt: this.effectiveAt || null,
      retiredAt: this.retiredAt || null,
      createdAt: this.createdAt || serverTimestamp(),
      createdBy: this.createdBy,
    };
  }

  static fromFirestore(docData) {
    return new ProductVersion(docData);
  }
}
